import type { Practice } from "services/types/clinics.type";
import type {
  EmailBranding,
  EmailBrandingKind,
  EmailBrandingPart,
  EmailBrandingParts
} from "services/types/email-branding.type";

export const EMAIL_BRANDING_SETTINGS_URL =
  "/settings/practice?tab=email-branding";

/** The backend's built-in notice, which `disclaimer_text: null` renders. */
export const DEFAULT_DISCLAIMER_TEXT =
  "This email contains confidential healthcare information and is intended solely for the use of the individual or entity to whom it is addressed. If you have received this email in error, please notify the sender immediately and delete it from your system.";

/** How to add the logo to a Designer email: the backend never adds it to one. */
export const DESIGNER_LOGO_HOW_TO =
  "Your logo is never added to emails built in the Designer. To show it, drag an Image block into the design and upload your logo.";

/** How to place the signature or disclaimer in a Designer email. */
export const DESIGNER_MERGE_TAG_HOW_TO =
  "To put your signature or disclaimer somewhere else, add the Email Signature or Email Disclaimer merge tag where you want it.";

/**
 * A Designer email that gets no branding parts: one a staff member writes (the checkbox does not
 * apply to it), or any Designer email while the practice has never saved its branding settings.
 */
export const DESIGNER_SENT_AS_DESIGNED_LINES: readonly string[] = [
  "Emails built in the Designer are sent exactly as you design them, without your logo, signature or disclaimer.",
  DESIGNER_LOGO_HOW_TO,
  "To add your signature or disclaimer, add the Email Signature or Email Disclaimer merge tag where you want it."
];

/** The settings page's warning about the Designer. */
export const DESIGNER_SETTINGS_WARNING =
  "Emails built in the Designer never get your logo: staff must drag an Image block into the design and upload it. They get the signature and disclaimer ticked above, at the bottom of the email, unless the Email Signature or Email Disclaimer merge tag places them somewhere else.";

/**
 * Places the practice's signature or disclaimer at a chosen spot instead of the end. The backend
 * resolves both for every email, so no client-side merge fill may blank them.
 */
export const EMAIL_SIGNATURE_MERGE_TAG = "email_signature";
export const EMAIL_DISCLAIMER_MERGE_TAG = "email_disclaimer";
export const BACKEND_ONLY_MERGE_TAGS: ReadonlySet<string> = new Set([
  EMAIL_SIGNATURE_MERGE_TAG,
  EMAIL_DISCLAIMER_MERGE_TAG
]);

/** A Designer merge-tag set without the two branding tags, for editors while email branding is off. */
export const withoutBrandingMergeTags = <T extends Record<string, unknown>>(
  tags: T
): T => {
  const {
    [EMAIL_SIGNATURE_MERGE_TAG]: _signature,
    [EMAIL_DISCLAIMER_MERGE_TAG]: _disclaimer,
    ...rest
  } = tags;
  return rest as T;
};

/** The classic editor's group for the two tags, for `WordProcessorField`'s `extraMergeTagsOptions`. */
export const EMAIL_BRANDING_MERGE_TAG_OPTIONS = [
  {
    title: "Email branding",
    menu: [
      { title: "Email branding - Signature", value: EMAIL_SIGNATURE_MERGE_TAG },
      {
        title: "Email branding - Disclaimer",
        value: EMAIL_DISCLAIMER_MERGE_TAG
      }
    ]
  }
];

/** The body the settings page previews its branding around. */
export const SAMPLE_EMAIL_BODY =
  "<p>Hi Jane,</p><p>This is how your emails will look with the branding you choose here.</p>";

/** Rows of the settings table, in display order, and their copy. */
export const EMAIL_BRANDING_KINDS: readonly {
  kind: EmailBrandingKind;
  label: string;
  description: string;
}[] = [
  {
    kind: "appointments",
    label: "Appointment notifications",
    description:
      "Confirmations, reminders, appointment details, reschedules, cancellations and no-shows"
  },
  {
    kind: "feedback",
    label: "Feedback and review requests",
    description:
      "Feedback surveys, bad-rating replies and Google review requests"
  },
  {
    kind: "forms",
    label: "Forms and intake",
    description:
      "Intake form requests and reminders, and forms shared to the portal"
  },
  {
    kind: "billing",
    label: "Billing",
    description:
      "Payment requests, receipts, collections, and bulk invoice and statement emails"
  },
  {
    kind: "marketing",
    label: "Marketing campaigns and bulk touchpoints",
    description: "Automated and custom campaigns, and bulk touchpoint emails"
  },
  {
    kind: "staff",
    label: "Staff notifications",
    description:
      "Task, comment and touchpoint assignments, @mentions and other alerts to your team"
  },
  {
    kind: "manual",
    label: "Emails your staff write (what 'Include email branding' adds)",
    description:
      "Emails sent from New Email: chart notes, invoices, documents and touchpoints"
  }
];

export const EMAIL_BRANDING_PARTS: readonly {
  part: EmailBrandingPart;
  label: string;
}[] = [
  { part: "logo", label: "Logo" },
  { part: "signature", label: "Signature" },
  { part: "disclaimer", label: "Disclaimer" }
];

/** Today's output, kept by any email no kind covers: the logo header and the confidentiality notice. */
export const TODAYS_PARTS: EmailBrandingParts = {
  logo: true,
  signature: false,
  disclaimer: true
};

export const DEFAULT_EMAIL_BRANDING: EmailBranding = {
  logo_source: "clinic",
  signature_html: null,
  disclaimer_text: null,
  manual_checked_by_default: false,
  clinic_overrides: {},
  categories: {
    appointments: TODAYS_PARTS,
    feedback: TODAYS_PARTS,
    forms: TODAYS_PARTS,
    billing: TODAYS_PARTS,
    marketing: TODAYS_PARTS,
    staff: TODAYS_PARTS,
    manual: TODAYS_PARTS
  }
};

/** The one place the defaults apply: `null`, or any key missing, reads as the default. */
export const resolveEmailBranding = (
  practice: Pick<Practice, "email_branding"> | null | undefined
): EmailBranding => {
  const stored = practice?.email_branding ?? {};
  const defaults = DEFAULT_EMAIL_BRANDING;
  const categories = Object.fromEntries(
    EMAIL_BRANDING_KINDS.map(({ kind }) => {
      const parts = stored.categories?.[kind] ?? {};
      return [
        kind,
        {
          logo: parts.logo ?? defaults.categories[kind].logo,
          signature: parts.signature ?? defaults.categories[kind].signature,
          disclaimer: parts.disclaimer ?? defaults.categories[kind].disclaimer
        }
      ];
    })
  ) as EmailBranding["categories"];

  return {
    logo_source: stored.logo_source ?? defaults.logo_source,
    signature_html: stored.signature_html ?? defaults.signature_html,
    disclaimer_text: stored.disclaimer_text ?? defaults.disclaimer_text,
    manual_checked_by_default:
      stored.manual_checked_by_default ?? defaults.manual_checked_by_default,
    categories,
    clinic_overrides: Object.fromEntries(
      Object.entries(stored.clinic_overrides ?? {}).map(
        ([clinicId, override]) => [
          clinicId,
          {
            signature_html: override?.signature_html ?? null,
            disclaimer_text: override?.disclaimer_text ?? null,
            reply_to: override?.reply_to ?? null
          }
        ]
      )
    ),
    // Carried even while the reply-to flag is off: the settings page saves the whole object, and
    // `toEmailBrandingPayload` strips it from that save when the flag is off.
    reply_to: stored.reply_to ?? null
  };
};

/** "logo, signature and disclaimer"; empty when no part is on. */
export const describeParts = (parts: EmailBrandingParts): string => {
  const names = EMAIL_BRANDING_PARTS.filter(({ part }) => parts[part]).map(
    ({ label }) => label.toLowerCase()
  );
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const SETTINGS_NOUN = "your practice's email branding settings";
const BASED_ON_SETTINGS = `based on ${SETTINGS_NOUN}`;

/** "logo and disclaimer are" / "logo is": the verb follows the number of parts named. */
const partsWithVerb = (parts: EmailBrandingParts): string => {
  const described = describeParts(parts);
  const count = EMAIL_BRANDING_PARTS.filter(({ part }) => parts[part]).length;
  return `${described} ${count > 1 ? "are" : "is"}`;
};

/**
 * The sentences of the branding banner on a surface where a practice setting decides. `parts`
 * undefined means the kind is not known on this surface (a reusable template), and
 * `hasSavedSettings` false means the backend still sends a Designer email exactly as designed.
 */
export const describeBrandingBanner = ({
  parts,
  isDesigner,
  hasSavedSettings
}: {
  parts: EmailBrandingParts | undefined;
  isDesigner: boolean;
  hasSavedSettings: boolean;
}): string[] => {
  if (!isDesigner) {
    if (!parts) {
      return [
        `When this template is sent, your logo, signature or disclaimer may be added, depending on the kind of email and ${SETTINGS_NOUN}.`
      ];
    }
    return describeParts(parts)
      ? [
          `Your ${partsWithVerb(parts)} added to this email, ${BASED_ON_SETTINGS}.`
        ]
      : [
          `No logo, signature or disclaimer is added to this email, ${BASED_ON_SETTINGS}.`
        ];
  }

  if (!hasSavedSettings) return [...DESIGNER_SENT_AS_DESIGNED_LINES];

  const bottomParts = parts && {
    logo: false,
    signature: parts.signature,
    disclaimer: parts.disclaimer
  };
  const added = !bottomParts
    ? `When this template is sent, your signature or disclaimer may be added at the bottom, depending on the kind of email and ${SETTINGS_NOUN}.`
    : describeParts(bottomParts)
      ? `Your ${partsWithVerb(bottomParts)} added at the bottom of this email, ${BASED_ON_SETTINGS}.`
      : `No signature or disclaimer is added to this email, ${BASED_ON_SETTINGS}.`;
  return [added, DESIGNER_LOGO_HOW_TO, DESIGNER_MERGE_TAG_HOW_TO];
};

/** The "Include email branding by default" description: names exactly what a tick adds. */
export const describeManualDefault = (parts: EmailBrandingParts): string =>
  describeParts(parts)
    ? `When someone on your team writes a new email, your ${partsWithVerb(parts)} already added. They can turn this off for any single email.`
    : 'When someone on your team writes a new email, the "Include email branding" box starts ticked. Tick a part on the "Emails your staff write" row above to choose what it adds.';
